import { generateHeader, POSTAPI } from "src/api-setup/api-helper";
import { GET_PAY_REQUEST_COUNT_BY_STATUS } from "src/api-setup/api-endpoints";
import { API_CONSTANTS } from "../../providerPayRequestDashboard/utils/constants";
import { CHART_COLORS } from "../components/charts/chartConstants";

/*
 * Pay request counts per status - the Request Status Overview stacked bars.
 *
 * Request:
 *   { in_tenant_id, in_provider_id, in_start_date, in_end_date,
 *     in_sort_number: 1, in_sort_order: "DESC" }
 *
 *   There is no grouping parameter: the endpoint returns one flat count per
 *   status for the window, which is why the card's granularity strip moves the
 *   window instead (see getGranularityRange).
 *
 * Response row:
 *   { status_value: "pending_comp_admin_review", display_name: "Comp Admin Review", count: 15 }
 *
 *   Only statuses with a non-zero count are returned, so the four the design
 *   draws are zero-filled below rather than read off the response.
 *
 * Normalized shape:
 *   { groupKey, groupLabel, total, segments: [{ key, label, value, color }] }[]
 */

/*
 * The design fixes both the status order and the colours, so statuses are
 * matched to it rather than trusting a server-supplied colour. Matching is on a
 * squashed lower-case name, which tolerates "In-Progress" / "in progress" etc.
 */
export const STATUS_META = [
  {
    key: "new",
    label: "New",
    color: CHART_COLORS.statusNew,
    group: "in_progress",
    aliases: ["new", "draft", "notsubmitted"],
  },
  {
    key: "approval_in_process",
    label: "Approval in Process",
    color: CHART_COLORS.statusApprovalInProcess,
    group: "in_progress",
    aliases: ["approvalinprocess", "pending", "pendingapproval", "submitted", "inapproval"],
  },
  {
    key: "pending_comp_admin_review",
    label: "Comp Admin Review",
    color: CHART_COLORS.statusCompAdminReview,
    group: "in_progress",
    aliases: ["pendingcompadminreview", "compadminreview", "adminreview", "compreview"],
  },
  {
    key: "rejected",
    label: "Rejected",
    color: CHART_COLORS.statusRejected,
    group: "rejected",
    aliases: ["rejected", "declined", "returned"],
  },
];

export const STATUS_GROUPS = [
  { key: "in_progress", label: "In Progress", totalColor: CHART_COLORS.inProgressTotal },
  { key: "rejected", label: "Rejected", totalColor: CHART_COLORS.rejectedTotal },
];

const squash = (value) => String(value ?? "").toLowerCase().replace(/[^a-z]/g, "");

const matchStatus = (name) => {
  const squashed = squash(name);
  return STATUS_META.find((meta) => meta.aliases.includes(squashed));
};

const toCount = (row) => {
  const parsed = Number(row?.count);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeStatusRows = (rows) => {
  const counts = new Map();
  const labels = new Map();

  rows.forEach((row) => {
    const meta = matchStatus(row?.status_value ?? row?.status ?? row?.display_name);

    // An unrecognised status is dropped rather than shown uncoloured; the
    // design has a fixed four-status legend.
    if (!meta) return;

    counts.set(meta.key, (counts.get(meta.key) || 0) + toCount(row));

    if (row?.display_name) labels.set(meta.key, String(row.display_name));
  });

  return STATUS_GROUPS.map((group) => {
    const segments = STATUS_META.filter((meta) => meta.group === group.key).map((meta) => ({
      key: meta.key,
      // The server's own wording wins when it sent this status at all; a status
      // it omitted (count zero) falls back to the design label.
      label: labels.get(meta.key) || meta.label,
      value: counts.get(meta.key) || 0,
      color: meta.color,
    }));

    return {
      groupKey: group.key,
      groupLabel: group.label,
      totalColor: group.totalColor,
      total: segments.reduce((sum, segment) => sum + segment.value, 0),
      segments,
    };
  });
};


export const getPayRequestCountByStatus = async (
  cookiesData,
  dispatch,
  { providerId, startDate, endDate, in_period_mstr_key } = {},
  scope = {}
) => {
  if (!cookiesData || !providerId || !startDate || !endDate) return [];

  try {
    const apiHeaders = generateHeader();
    const requestBody = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_provider_id: providerId,
      in_start_date: startDate,
      in_period_mstr_key: in_period_mstr_key,
      in_end_date: endDate,
      in_sort_number: API_CONSTANTS.DEFAULT_SORT_NUMBER,
      in_sort_order: API_CONSTANTS.DEFAULT_SORT_ORDER_DESC,
      ...scope,
    };

    const response = await POSTAPI(
      false,
      GET_PAY_REQUEST_COUNT_BY_STATUS,
      null,
      apiHeaders,
      requestBody,
      dispatch,
    );
    const returnCode = response?.data?.out_parameters?.out_ret_code;

    if (
      returnCode !== API_CONSTANTS.SUCCESS_CODE &&
      returnCode !== API_CONSTANTS.RECORDS_NOT_FOUND_CODE
    ) {
      return [];
    }

    const rowResult = response?.data?.row_result;

    if (!Array.isArray(rowResult)) return [];

    return normalizeStatusRows(rowResult);
  } catch (error) {
    console.error("Error fetching pay request count by status:", error);
    return [];
  }
};
